<template>
    <div class="free-cancellation-badge-wrapper">
        <Badge
            v-if="isRefundable"
            :size="size"
            color="green"
            iconPosition="right"
            icon="InfoCircle"
            :iconTooltip="t({ key: 'cancellation.free_cancellation_disclaimer' })"
            :text="freeCancellationText"
            :ariaLabel="freeCancellationText"
            @click="openCancellationDetails"
            @keydown.enter.stop.prevent="openCancellationDetails"
            tabindex="0" />
        <Badge
            v-else-if="!isTripSummary"
            :size="size"
            color="green"
            :text="nonRefundableText"
            :ariaLabel="nonRefundableText"
            @click="openCancellationDetails"
            @keydown.enter.stop.prevent="openCancellationDetails"
            tabindex="0" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, PropType } from 'vue';
    import { ActivityProduct, HotelProduct, ProductType } from '@/api/Cart/cart.interfaces';
    import { useStore } from '@/store';
    import { ProductModalTab, ModalType } from '@/store/general/types';
    import { useViewport } from '@shared/helpers/viewport';
    import { Size as BadgeSize } from '@ui/components/Badge/Badge.interfaces';
    import { logStatsigCheckoutPriceBreakdownBadgeOpen } from '@/tracking/statsig/checkout.functions';
    import { getCartProductCancellation } from '../Cancellation/CartCancellation/CartCancellation.helpers';
    import Badge from '@ui/components/Badge/Badge.vue';

    export default {
        name: 'PricePolicyBadge',
        methods: { t },
        components: { Badge },
        props: {
            product: {
                type: Object as PropType<ActivityProduct | HotelProduct | undefined>,
                required: false,
            },
            size: {
                type: String as PropType<BadgeSize>,
                default: 's',
            },
            isTripSummary: Boolean,
        },
        setup(props) {
            const store = useStore();
            const isMobile = computed<boolean>(() => useViewport('smaller', 'lg').value);
            const isHotel = props.product?.Type === ProductType.Hotel;
            const isGolf = props.product?.Type === ProductType.Golf;
            const isActivity = props.product?.Type === ProductType.Activity;

            const nonRefundableText = computed(() => {
                return isHotel
                    ? t({ key: 'shared.special_price', suffix: ' - ' }) +
                          '' +
                          t({ key: 'cancellation.non_refundable' })
                    : t({ key: 'cancellation.non_refundable' });
            });

            const freeCancellationText = t({ key: 'cancellation.free_cancellation' });

            const isRefundable = computed((): boolean => {
                if (isHotel || isGolf || isActivity) {
                    return getCartProductCancellation(props.product).Refundable;
                }
                return false;
            });

            function openCancellationDetails(e: MouseEvent) {
                if (e) {
                    const target = e.target as HTMLElement;
                    const iconSvgParent = target.closest('.icon-svg');

                    if (isMobile.value && iconSvgParent) {
                        e.stopPropagation();
                        return;
                    }
                }
                if (isHotel || isGolf) {
                    store.dispatch('general/setModal', {
                        type: ModalType.Product,
                        productId: props.product.Id,
                        activeTab: ProductModalTab.Terms,
                    });
                    logStatsigCheckoutPriceBreakdownBadgeOpen(ProductType.Hotel);
                }

                if (isActivity) {
                    store.dispatch('general/setModal', {
                        type: ModalType.Product,
                        productId: props.product.Id,
                        activeTab: ProductModalTab.Price,
                    });
                    logStatsigCheckoutPriceBreakdownBadgeOpen(ProductType.Activity);
                }
            }

            return {
                freeCancellationText,
                nonRefundableText,
                isRefundable,
                openCancellationDetails,
            };
        },
    };
</script>

<style lang="scss" scoped>
    .free-cancellation-badge-wrapper {
        width: fit-content;
        cursor: pointer;
    }
</style>
